import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { makeFunctionReference } from "convex/server";
import type { FunctionReference } from "convex/server";
import { api } from "../../convex/_generated/api";
import type { Doc, Id } from "../../convex/_generated/dataModel";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { ProgressRing } from "@/components/ui/ProgressRing";
import { EmptyState } from "@/components/ui/EmptyState";
import { Skeleton } from "@/components/ui/Skeleton";
import { Input } from "@/components/ui/Input";
import { StatCard } from "@/components/StatCard";
import { MealCard } from "@/components/MealCard";
import { ExerciseCard } from "@/components/ExerciseCard";
import { PlanProgressCard } from "@/components/PlanProgressCard";
import { DayStatusControl } from "@/components/progress/DayStatusControl";
import { WorkoutForm } from "@/components/WorkoutForm";
import { MealForm } from "@/components/MealForm";
import { FlowSheetShell } from "@/components/quicklog/FlowSheetShell";
import { cn } from "@/lib/cn";
import { formatMacroValue, formatNumber, formatTime } from "@/lib/format";
import { clientLogTimes, localDayStartMs, dayStartMs, addDays, formatDay } from "@/lib/dayHelpers";
import { formatKcalBalance } from "../lib/kcal";
import { useLoggingFlow } from "../lib/logging-flow";
import { adjustKnownMacroTotal, buildMacroPatch } from "./macro-edit";
import {
  ENTRY_ACTIONS,
  MEAL_GROUP_LABELS,
  MOVE_TARGET_TYPES,
  canCopyYesterday,
  countLoggedEntries,
  entryKcal,
  formatQuantityUnit,
  groupEntriesByMealType,
  unknownKcalNotice,
} from "./today-utils";
import type { MoveTargetType } from "./today-utils";
import {
  ChevronLeft,
  ChevronRight,
  Apple,
  Flame,
  Scale,
  Target,
  ArrowRight,
  Dumbbell,
  Camera,
  PenLine,
  ScanBarcode,
  Search,
  Copy,
} from "lucide-react";

type FoodEntry = Doc<"foodEntries">;
type ExerciseEntry = Doc<"exerciseEntries">;

type EditTarget =
  | { kind: "food"; entry: FoodEntry }
  | { kind: "exercise"; entry: ExerciseEntry };

type ActionTarget = "meal" | "exercise";

type UpdateFoodArgs = {
  readonly id: FoodEntry["_id"];
  readonly name?: string;
  readonly description?: string;
  readonly finalKcal?: number;
  readonly notes?: string;
  readonly proteinG?: number;
  readonly carbsG?: number;
  readonly fatG?: number;
  readonly fiberG?: number;
};

const updateFoodRef: FunctionReference<"mutation", "public", UpdateFoodArgs, null> =
  makeFunctionReference("entries:update");

export interface TodayProps {
  onSwitchScreen?: (screen: string) => void;
  // Test hooks (renderToStaticMarkup cannot click): pre-open the entry action
  // sheet on a specific entry, the move picker, an inline action error, or the
  // copy-yesterday confirmation sheet.
  initialActionEntry?: FoodEntry | null;
  initialActionError?: string | null;
  initialMovePickerOpen?: boolean;
  initialCopySheetOpen?: boolean;
}

function getFoodKcal(entry: FoodEntry): number {
  return entry.finalKcal ?? entry.estimatedKcal ?? 0;
}

function getExerciseKcal(entry: ExerciseEntry): number {
  return entry.finalBurnedKcal ?? entry.estimatedBurnedKcal;
}

function tempId<T extends "foodEntries" | "savedMeals" | "foodMemories" | "weightHistory">(table: T): Id<T> {
  void table;
  return `temp_${Date.now()}_${Math.random().toString(36).slice(2)}` as Id<T>;
}

function DatePicker({
  selectedDate,
  onChange,
}: {
  readonly selectedDate: number;
  readonly onChange: (date: number) => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <button
        type="button"
        aria-label="Previous day"
        className="flex h-11 w-11 items-center justify-center rounded-xl bg-surface text-text shadow-sm active:scale-[0.98] select-none"
        onClick={() => onChange(addDays(selectedDate, -1))}
      >
        <ChevronLeft size={22} strokeWidth={2} />
      </button>
      <span className="text-base font-semibold text-text">
        {formatDay(selectedDate)}
      </span>
      <button
        type="button"
        aria-label="Next day"
        className="flex h-11 w-11 items-center justify-center rounded-xl bg-surface text-text shadow-sm active:scale-[0.98] select-none"
        onClick={() => onChange(addDays(selectedDate, 1))}
      >
        <ChevronRight size={22} strokeWidth={2} />
      </button>
    </div>
  );
}

export default function Today({ onSwitchScreen, initialActionEntry = null, initialActionError = null, initialMovePickerOpen = false, initialCopySheetOpen = false }: TodayProps) {
  const { startFlow } = useLoggingFlow();
  const [selectedDate, setSelectedDate] = useState(() => localDayStartMs(Date.now()));
  const selectedDateRef = useRef(selectedDate);
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);
  const [formName, setFormName] = useState("");
  const [formKcal, setFormKcal] = useState("");
  const [formProtein, setFormProtein] = useState("");
  const [formCarbs, setFormCarbs] = useState("");
  const [formFat, setFormFat] = useState("");
  const [formFiber, setFormFiber] = useState("");
  const [formNotes, setFormNotes] = useState("");
  const [editError, setEditError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionTarget, setActionTarget] = useState<ActionTarget | null>(null);
  const [weightInput, setWeightInput] = useState("");
  const [weightError, setWeightError] = useState<string | null>(null);
  const [entryAction, setEntryAction] = useState<FoodEntry | null>(initialActionEntry);
  const [actionError, setActionError] = useState<string | null>(initialActionError);
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const [movePickerOpen, setMovePickerOpen] = useState(initialMovePickerOpen);
  const [actionConfirmDelete, setActionConfirmDelete] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [copySheetOpen, setCopySheetOpen] = useState(initialCopySheetOpen);
  const [copyError, setCopyError] = useState<string | null>(null);
  const [copyBusy, setCopyBusy] = useState(false);

  useEffect(() => {
    selectedDateRef.current = selectedDate;
    console.log("[Frontend][Today] Date changed", { date: selectedDate });
  }, [selectedDate]);

  const yesterdayDate = addDays(selectedDate, -1);
  const summary = useQuery(api.dashboard.getByDate, { date: selectedDate, clientDayStart: selectedDate });
  const yesterdaySummary = useQuery(api.dashboard.getByDate, { date: yesterdayDate, clientDayStart: yesterdayDate });
  const profile = useQuery(api.profile.getProfile, {});
  const engineState = useQuery(api.engine.getEngineState, {});
  const weights = useQuery(api.weight.list, { fromDate: selectedDate, toDate: selectedDate, clientFromDayStart: selectedDate, clientToDayStart: selectedDate });
  const logWeight = useMutation(api.weight.log).withOptimisticUpdate((localStore, args) => {
    const weightQuery = { fromDate: args.date, toDate: args.date, clientFromDayStart: args.clientDayStart, clientToDayStart: args.clientDayStart };
    const current = localStore.getQuery(api.weight.list, weightQuery);
    if (current === undefined) return;
    const profile = localStore.getQuery(api.profile.getProfile, {});
    if (profile === undefined || profile === null) return;
    const now = Date.now();
    localStore.setQuery(api.weight.list, weightQuery, [
      ...current,
      {
        _id: tempId("weightHistory"),
        _creationTime: now,
        userId: profile._id,
        date: args.clientDayStart ?? dayStartMs(args.date),
        weightKg: args.weightKg,
        loggedAt: args.clientLoggedAt ?? now,
        updatedAt: now,
      },
    ]);
  });

  const updateFood = useMutation(updateFoodRef).withOptimisticUpdate(
    (localStore, args) => {
      const date = selectedDateRef.current;
      const current = localStore.getQuery(api.dashboard.getByDate, { date, clientDayStart: date });
      if (current === undefined) return;
      const index = current.foodEntries.findIndex((entry) => entry._id === args.id);
      if (index === -1) return;
      const oldEntry = current.foodEntries[index] as FoodEntry;
      const oldKcal = getFoodKcal(oldEntry);
      const newFinalKcal = args.finalKcal !== undefined ? args.finalKcal : oldEntry.finalKcal;
      const newKcal = newFinalKcal ?? oldEntry.estimatedKcal ?? 0;
      const delta = newKcal - oldKcal;
      const patchedEntry: FoodEntry = {
        ...oldEntry,
        name: args.name ?? oldEntry.name,
        finalKcal: newFinalKcal,
        notes: args.notes !== undefined ? args.notes : oldEntry.notes,
        proteinG: args.proteinG !== undefined ? args.proteinG : oldEntry.proteinG,
        carbsG: args.carbsG !== undefined ? args.carbsG : oldEntry.carbsG,
        fatG: args.fatG !== undefined ? args.fatG : oldEntry.fatG,
        fiberG: args.fiberG !== undefined ? args.fiberG : oldEntry.fiberG,
      };
      localStore.setQuery(api.dashboard.getByDate, { date, clientDayStart: date }, {
        ...current,
        consumedKcal: current.consumedKcal + delta,
        consumedProteinG: adjustKnownMacroTotal(current.consumedProteinG, oldEntry.proteinG, args.proteinG),
        consumedCarbsG: adjustKnownMacroTotal(current.consumedCarbsG, oldEntry.carbsG, args.carbsG),
        consumedFatG: adjustKnownMacroTotal(current.consumedFatG, oldEntry.fatG, args.fatG),
        consumedFiberG: adjustKnownMacroTotal(current.consumedFiberG, oldEntry.fiberG, args.fiberG),
        netKcal: current.netKcal + delta,
        remainingKcal: current.remainingKcal - delta,
        foodEntries: current.foodEntries.map((entry, entryIndex) => entryIndex === index ? patchedEntry : entry),
      });
    },
  );

  const removeFood = useMutation(api.entries.remove).withOptimisticUpdate(
    (localStore, args) => {
      const date = selectedDateRef.current;
      const current = localStore.getQuery(api.dashboard.getByDate, { date, clientDayStart: date });
      if (current === undefined) return;
      const index = current.foodEntries.findIndex((entry) => entry._id === args.id);
      if (index === -1) return;
      const oldEntry = current.foodEntries[index] as FoodEntry;
      const oldKcal = getFoodKcal(oldEntry);
      localStore.setQuery(api.dashboard.getByDate, { date, clientDayStart: date }, {
        ...current,
        consumedKcal: current.consumedKcal - oldKcal,
        netKcal: current.netKcal - oldKcal,
        remainingKcal: current.remainingKcal + oldKcal,
        unknownKcalCount: current.unknownKcalCount - (entryKcal(oldEntry) === undefined ? 1 : 0),
        foodEntries: current.foodEntries.filter((_, entryIndex) => entryIndex !== index),
      });
    },
  );

  const updateExercise = useMutation(api.entries.updateExercise).withOptimisticUpdate(
    (localStore, args) => {
      const date = selectedDateRef.current;
      const current = localStore.getQuery(api.dashboard.getByDate, { date, clientDayStart: date });
      if (current === undefined) return;
      const index = current.exerciseEntries.findIndex((entry) => entry._id === args.id);
      if (index === -1) return;
      const oldEntry = current.exerciseEntries[index] as ExerciseEntry;
      const oldKcal = getExerciseKcal(oldEntry);
      const newFinalBurnedKcal = args.finalBurnedKcal !== undefined ? args.finalBurnedKcal : oldEntry.finalBurnedKcal;
      const newKcal = newFinalBurnedKcal ?? oldEntry.estimatedBurnedKcal;
      const delta = newKcal - oldKcal;
      const patchedEntry: ExerciseEntry = {
        ...oldEntry,
        type: args.type ?? oldEntry.type,
        finalBurnedKcal: newFinalBurnedKcal,
        notes: args.notes !== undefined ? args.notes : oldEntry.notes,
      };
      localStore.setQuery(api.dashboard.getByDate, { date, clientDayStart: date }, {
        ...current,
        burnedKcal: current.burnedKcal + delta,
        netKcal: current.netKcal - delta,
        remainingKcal: current.remainingKcal + delta,
        exerciseEntries: current.exerciseEntries.map((entry, entryIndex) => entryIndex === index ? patchedEntry : entry),
      });
    },
  );

  const removeExercise = useMutation(api.entries.removeExercise).withOptimisticUpdate(
    (localStore, args) => {
      const date = selectedDateRef.current;
      const current = localStore.getQuery(api.dashboard.getByDate, { date, clientDayStart: date });
      if (current === undefined) return;
      const index = current.exerciseEntries.findIndex((entry) => entry._id === args.id);
      if (index === -1) return;
      const oldEntry = current.exerciseEntries[index] as ExerciseEntry;
      const oldKcal = getExerciseKcal(oldEntry);
      localStore.setQuery(api.dashboard.getByDate, { date, clientDayStart: date }, {
        ...current,
        burnedKcal: current.burnedKcal - oldKcal,
        netKcal: current.netKcal + oldKcal,
        remainingKcal: current.remainingKcal - oldKcal,
        exerciseEntries: current.exerciseEntries.filter((_, entryIndex) => entryIndex !== index),
      });
    },
  );

  // Entry action: Duplicate (plan todo 20). The clone lands on the selected
  // day with a temp id; unknown-kcal entries duplicate without touching the
  // calorie sums and bump unknownKcalCount instead.
  const duplicateFood = useMutation(api.meals.duplicateEntry).withOptimisticUpdate(
    (localStore, args) => {
      const date = args.clientDayStart ?? (args.date !== undefined ? dayStartMs(args.date) : selectedDateRef.current);
      const queryArgs = { date, clientDayStart: date };
      const current = localStore.getQuery(api.dashboard.getByDate, queryArgs);
      if (current === undefined) return;
      const entry = current.foodEntries.find((candidate) => candidate._id === args.id) as FoodEntry | undefined;
      if (entry === undefined) return;
      const now = Date.now();
      const kcal = entryKcal(entry);
      const clone: FoodEntry = {
        ...entry,
        _id: tempId("foodEntries"),
        date,
        loggedAt: now,
        mealGroupId: undefined,
      };
      localStore.setQuery(api.dashboard.getByDate, queryArgs, {
        ...current,
        consumedKcal: current.consumedKcal + (kcal ?? 0),
        netKcal: current.netKcal + (kcal ?? 0),
        remainingKcal: current.remainingKcal - (kcal ?? 0),
        consumedProteinG: adjustKnownMacroTotal(current.consumedProteinG, undefined, entry.proteinG),
        consumedCarbsG: adjustKnownMacroTotal(current.consumedCarbsG, undefined, entry.carbsG),
        consumedFatG: adjustKnownMacroTotal(current.consumedFatG, undefined, entry.fatG),
        consumedFiberG: adjustKnownMacroTotal(current.consumedFiberG, undefined, entry.fiberG),
        unknownKcalCount: current.unknownKcalCount + (kcal === undefined ? 1 : 0),
        foodEntries: [...current.foodEntries, clone],
      });
    },
  );

  // Entry action: Move to another meal. Totals are unchanged — only the
  // mealType grouping moves; the stale mealGroupId is dropped (the backend
  // clears it the same way when no target group is passed).
  const moveFood = useMutation(api.meals.moveEntryToMeal).withOptimisticUpdate(
    (localStore, args) => {
      const date = selectedDateRef.current;
      const queryArgs = { date, clientDayStart: date };
      const current = localStore.getQuery(api.dashboard.getByDate, queryArgs);
      if (current === undefined) return;
      localStore.setQuery(api.dashboard.getByDate, queryArgs, {
        ...current,
        foodEntries: current.foodEntries.map((entry) =>
          entry._id === args.id
            ? { ...(entry as FoodEntry), mealType: args.mealType, mealGroupId: undefined }
            : entry,
        ),
      });
    },
  );

  // Copy yesterday: explicit sheet confirm only, never silent. Optimistically
  // appends yesterday's logged entries onto the selected day, preserving
  // unknown calories as unknown.
  const copyDayMutation = useMutation(api.meals.copyDay).withOptimisticUpdate(
    (localStore, args) => {
      const fromDate = args.clientFromDayStart ?? dayStartMs(args.fromDate);
      const toDate = args.clientToDayStart ?? dayStartMs(args.toDate);
      const source = localStore.getQuery(api.dashboard.getByDate, { date: fromDate, clientDayStart: fromDate });
      const target = localStore.getQuery(api.dashboard.getByDate, { date: toDate, clientDayStart: toDate });
      if (source === undefined || target === undefined) return;
      const now = Date.now();
      const logged = (source.foodEntries as FoodEntry[]).filter((entry) => entry.status === "logged");
      let kcalDelta = 0;
      let unknownDelta = 0;
      const clones: FoodEntry[] = logged.map((entry) => {
        const kcal = entryKcal(entry);
        if (kcal === undefined) {
          unknownDelta += 1;
        } else {
          kcalDelta += kcal;
        }
        return {
          ...entry,
          _id: tempId("foodEntries"),
          date: toDate,
          loggedAt: now,
          mealGroupId: undefined,
        };
      });
      localStore.setQuery(api.dashboard.getByDate, { date: toDate, clientDayStart: toDate }, {
        ...target,
        consumedKcal: target.consumedKcal + kcalDelta,
        netKcal: target.netKcal + kcalDelta,
        remainingKcal: target.remainingKcal - kcalDelta,
        consumedProteinG: logged.reduce((total, entry) => adjustKnownMacroTotal(total, undefined, entry.proteinG), target.consumedProteinG),
        consumedCarbsG: logged.reduce((total, entry) => adjustKnownMacroTotal(total, undefined, entry.carbsG), target.consumedCarbsG),
        consumedFatG: logged.reduce((total, entry) => adjustKnownMacroTotal(total, undefined, entry.fatG), target.consumedFatG),
        consumedFiberG: logged.reduce((total, entry) => adjustKnownMacroTotal(total, undefined, entry.fiberG), target.consumedFiberG),
        unknownKcalCount: target.unknownKcalCount + unknownDelta,
        foodEntries: [...target.foodEntries, ...clones],
      });
    },
  );

  // Entry action: Save to library. The saved-meals list is not subscribed on
  // this screen, so the optimistic insert only lands when the cache is warm
  // (guarded per the optimistic-update rules); the temp row mirrors the
  // server snapshot shape from todo 4.
  const saveToLibrary = useMutation(api.savedMeals.saveFromEntries).withOptimisticUpdate(
    (localStore, args) => {
      const current = localStore.getQuery(api.savedMeals.list, {});
      if (current === undefined) return;
      const profileDoc = localStore.getQuery(api.profile.getProfile, {});
      if (profileDoc === undefined || profileDoc === null) return;
      const date = selectedDateRef.current;
      const daySummary = localStore.getQuery(api.dashboard.getByDate, { date, clientDayStart: date });
      const entries = args.entryIds
        .map((id) => daySummary?.foodEntries.find((entry) => entry._id === id) as FoodEntry | undefined)
        .filter((entry): entry is FoodEntry => entry !== undefined);
      const now = Date.now();
      const items = entries.map((entry) => ({
        name: entry.name,
        ...(entry.quantity !== undefined ? { quantity: entry.quantity } : {}),
        ...(entry.unit !== undefined ? { unit: entry.unit } : {}),
        ...(entry.estimatedKcal !== undefined ? { estimatedKcal: entry.estimatedKcal } : {}),
        ...(entry.finalKcal !== undefined ? { finalKcal: entry.finalKcal } : {}),
        ...(entry.proteinG !== undefined ? { proteinG: entry.proteinG } : {}),
        ...(entry.carbsG !== undefined ? { carbsG: entry.carbsG } : {}),
        ...(entry.fatG !== undefined ? { fatG: entry.fatG } : {}),
        ...(entry.fiberG !== undefined ? { fiberG: entry.fiberG } : {}),
        ...(entry.confidence !== undefined ? { confidence: entry.confidence } : {}),
        ...(entry.dataSource !== undefined ? { dataSource: entry.dataSource } : {}),
        ...(entry.notes !== undefined ? { notes: entry.notes } : {}),
        ...(entry.kcalUnknown !== undefined ? { kcalUnknown: entry.kcalUnknown } : {}),
      }));
      const allKnown = items.every(
        (item) => item.kcalUnknown !== true && (item.finalKcal ?? item.estimatedKcal) !== undefined,
      );
      const totalCalories = allKnown
        ? items.reduce((sum, item) => sum + ((item.finalKcal ?? item.estimatedKcal) as number), 0)
        : undefined;
      localStore.setQuery(api.savedMeals.list, {}, [
        {
          _id: tempId("savedMeals"),
          _creationTime: now,
          userId: profileDoc._id,
          name: args.name,
          ...(args.mealType !== undefined ? { mealType: args.mealType } : {}),
          itemsJson: JSON.stringify(items),
          ...(totalCalories !== undefined ? { totalCalories } : {}),
          favorite: false,
          createdAt: now,
          lastUsedAt: now,
          timesUsed: 0,
        },
        ...current,
      ]);
    },
  );

  // Entry action: Save as memory (upsert on normalized name, mirroring the
  // backend addManual semantics). Only reachable when calories are known.
  const saveAsMemory = useMutation(api.foodMemory.addManual).withOptimisticUpdate(
    (localStore, args) => {
      const current = localStore.getQuery(api.foodMemory.listAll, {});
      if (current === undefined) return;
      const profileDoc = localStore.getQuery(api.profile.getProfile, {});
      if (profileDoc === undefined || profileDoc === null) return;
      const now = Date.now();
      const normalized = args.foodName.trim().toLowerCase();
      const existing = current.find((memory) => memory.foodNameLower === normalized);
      if (existing !== undefined) {
        localStore.setQuery(api.foodMemory.listAll, {}, current.map((memory) =>
          memory.foodNameLower === normalized
            ? {
                ...memory,
                calories: args.calories,
                confidence: "high",
                servingSize: args.servingSize ?? memory.servingSize,
                notes: args.notes ?? memory.notes,
                proteinG: args.proteinG ?? memory.proteinG,
                carbsG: args.carbsG ?? memory.carbsG,
                fatG: args.fatG ?? memory.fatG,
                fiberG: args.fiberG ?? memory.fiberG,
                lastUsedAt: now,
              }
            : memory,
        ));
        return;
      }
      localStore.setQuery(api.foodMemory.listAll, {}, [
        {
          _id: tempId("foodMemories"),
          _creationTime: now,
          userId: profileDoc._id,
          foodNameLower: normalized,
          foodName: args.foodName.trim(),
          ...(args.servingSize !== undefined ? { servingSize: args.servingSize } : {}),
          calories: args.calories,
          ...(args.notes !== undefined ? { notes: args.notes } : {}),
          ...(args.proteinG !== undefined ? { proteinG: args.proteinG } : {}),
          ...(args.carbsG !== undefined ? { carbsG: args.carbsG } : {}),
          ...(args.fatG !== undefined ? { fatG: args.fatG } : {}),
          ...(args.fiberG !== undefined ? { fiberG: args.fiberG } : {}),
          confidence: "high",
          source: "manual",
          createdAt: now,
          lastUsedAt: now,
          timesUsed: 0,
        },
        ...current,
      ]);
    },
  );

  useEffect(() => {
    if (!editTarget) return;
    setFormName(editTarget.kind === "food" ? editTarget.entry.name : editTarget.entry.type);
    // Unknown calories prefill blank, never 0 — the save handler requires a
    // real value before anything persists.
    if (editTarget.kind === "food") {
      const kcal = entryKcal(editTarget.entry);
      setFormKcal(kcal === undefined ? "" : String(kcal));
    } else {
      setFormKcal(String(getExerciseKcal(editTarget.entry)));
    }
    if (editTarget.kind === "food") {
      setFormProtein(editTarget.entry.proteinG !== undefined ? String(editTarget.entry.proteinG) : "");
      setFormCarbs(editTarget.entry.carbsG !== undefined ? String(editTarget.entry.carbsG) : "");
      setFormFat(editTarget.entry.fatG !== undefined ? String(editTarget.entry.fatG) : "");
      setFormFiber(editTarget.entry.fiberG !== undefined ? String(editTarget.entry.fiberG) : "");
    } else {
      setFormProtein("");
      setFormCarbs("");
      setFormFat("");
      setFormFiber("");
    }
    setFormNotes(editTarget.entry.notes ?? "");
    setEditError(null);
    setConfirmingDelete(false);
  }, [editTarget]);

  const handleClose = () => {
    setEditTarget(null);
    setConfirmingDelete(false);
  };

  const handleActionClose = () => {
    if (saving) return;
    setActionTarget(null);
  };

  const openEntryActions = (entry: FoodEntry) => {
    console.debug("[Frontend][Today] Entry action sheet opened", { entryId: entry._id, mealType: entry.mealType });
    setEntryAction(entry);
    setActionError(null);
    setActionNotice(null);
    setMovePickerOpen(false);
    setActionConfirmDelete(false);
  };

  const closeEntryActions = () => {
    setEntryAction(null);
    setActionError(null);
    setActionNotice(null);
    setMovePickerOpen(false);
    setActionConfirmDelete(false);
  };

  const runDuplicate = async () => {
    if (!entryAction) return;
    setActionBusy(true);
    setActionError(null);
    setActionNotice(null);
    console.log("[Frontend][Today] Duplicating entry", { entryId: entryAction._id, date: selectedDate });
    try {
      await duplicateFood({ id: entryAction._id, date: selectedDate, clientDayStart: selectedDate });
      closeEntryActions();
    } catch (err) {
      console.error("[Frontend][Today] Duplicate failed", { entryId: entryAction._id, error: err instanceof Error ? err.message : String(err) });
      setActionError("Could not duplicate that entry. Try again.");
    } finally {
      setActionBusy(false);
    }
  };

  const runMove = async (mealType: MoveTargetType) => {
    if (!entryAction) return;
    setActionBusy(true);
    setActionError(null);
    setActionNotice(null);
    console.log("[Frontend][Today] Moving entry", { entryId: entryAction._id, mealType });
    try {
      await moveFood({ id: entryAction._id, mealType });
      closeEntryActions();
    } catch (err) {
      // The entry stays in its original group (optimistic rollback); the
      // inline error keeps the failure explicit.
      console.error("[Frontend][Today] Move failed", { entryId: entryAction._id, mealType, error: err instanceof Error ? err.message : String(err) });
      setActionError("Could not move that entry. It stayed in its original meal.");
    } finally {
      setActionBusy(false);
    }
  };

  const runSaveToLibrary = async () => {
    if (!entryAction) return;
    setActionBusy(true);
    setActionError(null);
    setActionNotice(null);
    console.log("[Frontend][Today] Saving entry to library", { entryId: entryAction._id });
    try {
      await saveToLibrary({
        entryIds: [entryAction._id],
        name: entryAction.name,
        ...(entryAction.mealType !== undefined ? { mealType: entryAction.mealType } : {}),
      });
      setActionNotice("Saved to your library.");
    } catch (err) {
      console.error("[Frontend][Today] Save to library failed", { entryId: entryAction._id, error: err instanceof Error ? err.message : String(err) });
      setActionError("Could not save to the library. Try again.");
    } finally {
      setActionBusy(false);
    }
  };

  const runSaveAsMemory = async () => {
    if (!entryAction) return;
    const kcal = entryKcal(entryAction);
    if (kcal === undefined) {
      console.warn("[Frontend][Today] Save as memory blocked: calories unknown", { entryId: entryAction._id });
      setActionError("Calories are unknown — edit the entry to add calories before saving a memory.");
      setActionNotice(null);
      return;
    }
    setActionBusy(true);
    setActionError(null);
    setActionNotice(null);
    const servingSize = formatQuantityUnit(entryAction.quantity, entryAction.unit) ?? undefined;
    console.log("[Frontend][Today] Saving entry as memory", { entryId: entryAction._id, name: entryAction.name });
    try {
      await saveAsMemory({
        foodName: entryAction.name,
        calories: kcal,
        ...(servingSize !== undefined ? { servingSize } : {}),
        ...(entryAction.notes !== undefined ? { notes: entryAction.notes } : {}),
        ...(entryAction.proteinG !== undefined ? { proteinG: entryAction.proteinG } : {}),
        ...(entryAction.carbsG !== undefined ? { carbsG: entryAction.carbsG } : {}),
        ...(entryAction.fatG !== undefined ? { fatG: entryAction.fatG } : {}),
        ...(entryAction.fiberG !== undefined ? { fiberG: entryAction.fiberG } : {}),
      });
      setActionNotice("Saved as a food memory.");
    } catch (err) {
      console.error("[Frontend][Today] Save as memory failed", { entryId: entryAction._id, error: err instanceof Error ? err.message : String(err) });
      setActionError("Could not save that memory. Try again.");
    } finally {
      setActionBusy(false);
    }
  };

  const runActionDelete = async () => {
    if (!entryAction) return;
    if (!actionConfirmDelete) {
      setActionConfirmDelete(true);
      return;
    }
    setActionBusy(true);
    setActionError(null);
    console.log("[Frontend][Today] Deleting entry from action sheet", { entryId: entryAction._id });
    try {
      await removeFood({ id: entryAction._id });
      closeEntryActions();
    } catch (err) {
      console.error("[Frontend][Today] Delete failed", { entryId: entryAction._id, error: err instanceof Error ? err.message : String(err) });
      setActionError("Could not delete that entry. Try again.");
      setActionConfirmDelete(false);
    } finally {
      setActionBusy(false);
    }
  };

  const runCopyYesterday = async () => {
    setCopyBusy(true);
    setCopyError(null);
    console.log("[Frontend][Today] Copy yesterday confirmed", { fromDate: yesterdayDate, toDate: selectedDate });
    try {
      await copyDayMutation({
        fromDate: yesterdayDate,
        toDate: selectedDate,
        clientFromDayStart: yesterdayDate,
        clientToDayStart: selectedDate,
      });
      setCopySheetOpen(false);
    } catch (err) {
      console.error("[Frontend][Today] Copy yesterday failed", { error: err instanceof Error ? err.message : String(err) });
      setCopyError("Could not copy yesterday. Try again.");
    } finally {
      setCopyBusy(false);
    }
  };

  const handleSave = async () => {
    if (!editTarget) return;
    if (formKcal.trim().length === 0) {
      setEditError("Enter the calories — unknown stays unknown, it is never saved as 0.");
      return;
    }
    const kcal = Number(formKcal);
    if (!Number.isFinite(kcal) || kcal < 0) {
      setEditError("Enter a valid non-negative calorie amount.");
      return;
    }
    setSaving(true);
    console.log("[Frontend][Today] Entry edited", { entryId: editTarget.entry._id, kind: editTarget.kind, name: formName, kcal });
    try {
      if (editTarget.kind === "food") {
        const macroPatch = buildMacroPatch(editTarget.entry, {
          protein: formProtein,
          carbs: formCarbs,
          fat: formFat,
          fiber: formFiber,
        });
        if (macroPatch === null) {
          setEditError("Enter non-negative numbers for each macro value.");
          console.warn("[Frontend][Today] Invalid macro edit input", {
            entryId: editTarget.entry._id,
          });
          return;
        }
        await updateFood({
          id: editTarget.entry._id,
          name: formName,
          finalKcal: kcal,
          notes: formNotes,
          ...macroPatch,
        });
      } else {
        await updateExercise({ id: editTarget.entry._id, type: formName, finalBurnedKcal: kcal, notes: formNotes });
      }
      handleClose();
    } catch (err) {
      console.error("[Frontend][Today] Save failed", { entryId: editTarget.entry._id, error: err instanceof Error ? err.message : String(err) });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editTarget) return;
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    setSaving(true);
    try {
      if (editTarget.kind === "food") {
        await removeFood({ id: editTarget.entry._id });
      } else {
        await removeExercise({ id: editTarget.entry._id });
      }
      handleClose();
    } catch (err) {
      console.error("[Frontend][Today] Delete failed", { entryId: editTarget.entry._id, error: err instanceof Error ? err.message : String(err) });
    } finally {
      setSaving(false);
    }
  };

  const ringStats = useMemo(() => {
    const consumed = summary?.consumedKcal ?? 0;
    const goal = summary?.goalKcal ?? 0;
    const pct = goal > 0 ? consumed / goal : 0;
    return {
      consumed,
      goal,
      pct: Math.min(100, pct * 100),
      color: pct < 0.85 ? "#16a34a" : pct <= 1 ? "#f59e0b" : "#dc2626",
    };
  }, [summary]);

  const remainingColor = (summary?.remainingKcal ?? 0) < 0 ? "danger" : "accent";

  const macroRings = useMemo(() => {
    const build = (name: string, consumed: number | null | undefined, goal: number | null | undefined, color: string) => {
      const unknown = consumed === null || consumed === undefined;
      const consumedValue = consumed ?? 0;
      const hasGoal = typeof goal === "number" && goal > 0;
      return {
        name,
        unknown,
        color: unknown ? "#617068" : color,
        consumedText: formatMacroValue(unknown ? undefined : consumedValue, "", 0),
        goalText: hasGoal ? formatNumber(Math.round(goal)) : "—",
        pct: !unknown && hasGoal ? Math.max(0, Math.min(100, (consumedValue / goal) * 100)) : 0,
      };
    };
    return [
      build("Protein", summary?.consumedProteinG, summary?.goalProteinG, "#8b5cf6"),
      build("Carbs", summary?.consumedCarbsG, summary?.goalCarbsG, "#f59e0b"),
      build("Fat", summary?.consumedFatG, summary?.goalFatG, "#ec4899"),
      build("Fiber", summary?.consumedFiberG, summary?.goalFiberG, "#0f766e"),
    ];
  }, [summary]);

  useEffect(() => {
    if (!summary) return;
    console.debug("[Frontend][Today] Macro ring stats", {
      protein: { consumed: summary.consumedProteinG, goal: summary.goalProteinG },
      carbs: { consumed: summary.consumedCarbsG, goal: summary.goalCarbsG },
      fat: { consumed: summary.consumedFatG, goal: summary.goalFatG },
      fiber: { consumed: summary.consumedFiberG, goal: summary.goalFiberG },
    });
  }, [summary]);

  const currentWeight = weights && weights.length > 0 ? weights[weights.length - 1] : undefined;
  const saveWeight = async () => {
    const value = Number(weightInput);
    if (!Number.isFinite(value) || value <= 0) { setWeightError("Enter a positive weight in kg."); return; }
    setWeightError(null);
    try {
      await logWeight({ date: selectedDate, weightKg: value, ...clientLogTimes(selectedDate) });
      setWeightInput("");
      console.log("[Frontend][Today] Weight saved", { date: selectedDate, entryCount: (weights?.length ?? 0) + 1 });
    } catch (error: unknown) {
      console.error("[Frontend][Today] Weight save failed", { error: error instanceof Error ? error.message : String(error) });
      setWeightError("Weight could not be saved. Try again.");
    }
  };

  if (summary === undefined) {
    return (
      <div data-testid="today-container" className="flex flex-col gap-6">
        <DatePicker selectedDate={selectedDate} onChange={setSelectedDate} />
        <div className="flex justify-center py-2"><Skeleton className="h-40 w-40 rounded-full" /></div>
        <div className="flex items-start justify-between px-2">
          <Skeleton className="h-[72px] w-[72px] rounded-full" />
          <Skeleton className="h-[72px] w-[72px] rounded-full" />
          <Skeleton className="h-[72px] w-[72px] rounded-full" />
          <Skeleton className="h-[72px] w-[72px] rounded-full" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Skeleton className="h-20 rounded-2xl" />
          <Skeleton className="h-20 rounded-2xl" />
          <Skeleton className="h-20 rounded-2xl" />
          <Skeleton className="h-20 rounded-2xl" />
        </div>
        <Skeleton className="h-64 rounded-2xl" />
      </div>
    );
  }

  const isEmpty = summary.foodEntries.length === 0 && summary.exerciseEntries.length === 0;
  const foodEntries = summary.foodEntries as FoodEntry[];
  const mealGroups = groupEntriesByMealType(foodEntries);
  const yesterdayFood = yesterdaySummary === undefined || yesterdaySummary === null
    ? []
    : (yesterdaySummary.foodEntries as FoodEntry[]);
  const yesterdayLogged = yesterdayFood.filter((entry) => entry.status === "logged");
  const showCopyYesterday = canCopyYesterday({
    yesterdayLoggedCount: yesterdayLogged.length,
    todayLoggedCount: countLoggedEntries(foodEntries),
  });

  return (
    <div data-testid="today-container" className="flex flex-col gap-6">
      <DatePicker selectedDate={selectedDate} onChange={setSelectedDate} />

      <div className="flex flex-col items-center gap-3">
        <div className="flex justify-center" data-testid="progress-ring">
          <ProgressRing value={ringStats.pct} size={180} stroke={12} color={ringStats.color}>
            <div className="text-center">
              <div className="text-4xl font-bold text-text">{formatNumber(ringStats.consumed)}</div>
              <div className="text-sm text-text-muted">of {ringStats.goal > 0 ? formatNumber(ringStats.goal) : "—"}</div>
            </div>
          </ProgressRing>
        </div>
        <div className="flex w-full items-start justify-between px-2" data-testid="macro-rings">
          {macroRings.map((macro) => (
            <div key={macro.name} className="flex flex-col items-center gap-1">
              <ProgressRing value={macro.pct} size={72} stroke={7} color={macro.color}>
                <div className="text-center">
                  <div className="text-xs font-bold text-text">{macro.consumedText}</div>
                  {!macro.unknown && <div className="text-[10px] font-medium text-text-muted">g</div>}
                </div>
              </ProgressRing>
              <div className="text-center text-xs leading-tight">
                <div className="font-medium text-text">{macro.name}</div>
                <div className="text-text-muted">{macro.consumedText}/{macro.goalText}g</div>
              </div>
            </div>
          ))}
        </div>
        {summary.unknownKcalCount > 0 && (
          <p data-testid="unknown-kcal-notice" className="w-full rounded-xl bg-warning/10 px-3 py-2 text-sm font-medium text-warning">
            {unknownKcalNotice(summary.unknownKcalCount)}
          </p>
        )}
      </div>

      <PlanProgressCard profile={profile} />

      <div className="grid grid-cols-2 gap-3">
        <StatCard icon={Apple} label="Consumed" value={formatNumber(summary.consumedKcal)} unit="kcal" color="accent" testId="stat-card-consumed" />
        <StatCard icon={Flame} label="Burned" value={formatNumber(summary.burnedKcal)} unit="kcal" color="warning" testId="stat-card-burned" />
        <StatCard icon={Scale} label="Net" value={formatNumber(summary.netKcal)} unit="kcal" color="text-muted" testId="stat-card-net" />
        <StatCard icon={Target} label="Remaining" value={formatKcalBalance(summary.remainingKcal)} color={remainingColor} testId="stat-card-remaining" />
      </div>

      <Card data-testid="today-actions" className="p-4">
        <div className="flex flex-col gap-3">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">Log</h2>
            <p className="mt-1 text-sm text-text-muted">Quick capture or log directly</p>
          </div>
          <div className="grid grid-cols-4 gap-2" data-testid="quick-log-actions">
            <button
              type="button"
              data-testid="quick-log-photo"
              className="flex min-h-[44px] flex-col items-center justify-center gap-1 rounded-xl bg-muted py-2 text-text transition-all duration-200 ease-out active:scale-[0.98] select-none"
              onClick={() => { console.log("[Frontend][Today] Quick action: photo", { date: selectedDate }); startFlow("photo"); }}
            >
              <Camera size={18} strokeWidth={2} />
              <span className="text-[11px] font-medium">Photo</span>
            </button>
            <button
              type="button"
              data-testid="quick-log-describe"
              className="flex min-h-[44px] flex-col items-center justify-center gap-1 rounded-xl bg-muted py-2 text-text transition-all duration-200 ease-out active:scale-[0.98] select-none"
              onClick={() => { console.log("[Frontend][Today] Quick action: describe", { date: selectedDate }); startFlow("text"); }}
            >
              <PenLine size={18} strokeWidth={2} />
              <span className="text-[11px] font-medium">Describe</span>
            </button>
            <button
              type="button"
              data-testid="quick-log-barcode"
              className="flex min-h-[44px] flex-col items-center justify-center gap-1 rounded-xl bg-muted py-2 text-text transition-all duration-200 ease-out active:scale-[0.98] select-none"
              onClick={() => { console.log("[Frontend][Today] Quick action: barcode", { date: selectedDate }); startFlow("barcode"); }}
            >
              <ScanBarcode size={18} strokeWidth={2} />
              <span className="text-[11px] font-medium">Barcode</span>
            </button>
            <button
              type="button"
              data-testid="quick-log-search"
              className="flex min-h-[44px] flex-col items-center justify-center gap-1 rounded-xl bg-muted py-2 text-text transition-all duration-200 ease-out active:scale-[0.98] select-none"
              onClick={() => { console.log("[Frontend][Today] Quick action: search", { date: selectedDate }); startFlow("search"); }}
            >
              <Search size={18} strokeWidth={2} />
              <span className="text-[11px] font-medium">Search</span>
            </button>
          </div>
          {showCopyYesterday && (
            <button
              type="button"
              data-testid="copy-yesterday-btn"
              className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl border border-border bg-surface px-4 text-sm font-medium text-text transition-all duration-200 ease-out active:scale-[0.98] select-none"
              onClick={() => { console.debug("[Frontend][Today] Copy yesterday sheet opened", { fromDate: yesterdayDate }); setCopyError(null); setCopySheetOpen(true); }}
            >
              <Copy size={16} strokeWidth={2} />
              Copy yesterday
            </button>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Button
              data-testid="log-meal-btn"
              variant="secondary"
              size="md"
              className="w-full px-2 text-sm"
              onClick={() => { console.debug("[Frontend][Today] Opening quick-add meal sheet", { date: selectedDate }); setActionTarget("meal"); }}
            >
              <Apple size={17} strokeWidth={2} />
              Log meal
            </Button>
            <Button
              data-testid="log-exercise-btn"
              variant="secondary"
              size="md"
              className="w-full px-2 text-sm"
              onClick={() => { console.debug("[Frontend][Today] Opening exercise action sheet", { date: selectedDate }); setActionTarget("exercise"); }}
            >
              <Dumbbell size={17} strokeWidth={2} />
              Log exercise
            </Button>
          </div>
        </div>
      </Card>

      {isEmpty && (
        <div data-testid="empty-state-today" className="flex flex-col gap-4">
          <EmptyState
            icon={Apple}
            title="No meals or workouts yet"
            message="Log your first meal or exercise directly here, or tell the agent what you ate."
            action={<Button data-testid="empty-state-cta" variant="ghost" size="md" onClick={() => onSwitchScreen?.("chat")}>Chat with Agent<ArrowRight size={18} strokeWidth={2} /></Button>}
          />
        </div>
      )}

      {mealGroups.length > 0 && (
        <div className="flex flex-col gap-4" data-testid="meals-groups">
          {mealGroups.map((group) => (
            <section key={group.key} className="flex flex-col gap-2" data-testid={`meal-group-${group.key}`}>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">{group.label}</h2>
              <div className="flex flex-col gap-2">
                {group.entries.map((entry) => (
                  <MealCard key={entry._id} entry={entry} onEdit={openEntryActions} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {summary.exerciseEntries.length > 0 && (
        <div className="flex flex-col gap-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">Exercise</h2>
          <div className="flex flex-col gap-2">
            {summary.exerciseEntries.map((entry) => <ExerciseCard key={entry._id} entry={entry as ExerciseEntry} onEdit={(exerciseEntry) => setEditTarget({ kind: "exercise", entry: exerciseEntry })} />)}
          </div>
        </div>
      )}

      <Card className="flex flex-col gap-3 p-4" data-testid="today-weight-card">
        <div className="flex items-center justify-between gap-3"><div><h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">Body weight</h2><p className="mt-1 text-sm text-text-muted">Selected day measurement</p></div><div className="text-right"><span className="text-lg font-bold text-accent">{currentWeight ? `${currentWeight.weightKg} kg` : "—"}</span>{engineState?.trendKg != null && (<p className="text-xs text-text-muted" data-testid="trend-weight">Trend: {engineState.trendKg.toFixed(1)} kg</p>)}</div></div>
        <div className="flex items-end gap-2"><Input label="Weight (kg)" type="number" inputMode="decimal" min="1" step="0.1" value={weightInput} onChange={(event) => { setWeightInput(event.target.value); setWeightError(null); }} placeholder="82.0" /><Button size="sm" className="shrink-0" onClick={() => void saveWeight()}>Save</Button></div>
        {weightError && <p className="text-sm text-danger" role="alert">{weightError}</p>}
        {weights && weights.length > 1 && (
          <div className="flex max-h-28 flex-col gap-1 overflow-y-auto rounded-xl bg-muted/60 px-3 py-2" data-testid="weight-entries">
            {[...weights].reverse().map((entry) => (
              <div key={entry._id} className="flex items-center justify-between text-xs text-text-muted">
                <span>{formatTime(entry.loggedAt ?? entry.updatedAt)}</span>
                <span className="font-medium text-text">{entry.weightKg} kg</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <DayStatusControl date={selectedDate} status={summary.dayStatus} />

      {actionTarget && (
        <FlowSheetShell
          title={actionTarget === "meal" ? "Quick add" : "Log exercise"}
          onClose={handleActionClose}
          testId={`${actionTarget}-sheet`}
          zLayer="z-50"
        >
          {actionTarget === "meal" ? (
            <MealForm flow="quickAdd" date={selectedDate} onClose={handleActionClose} />
          ) : (
            <WorkoutForm date={selectedDate} onSaved={handleActionClose} />
          )}
        </FlowSheetShell>
      )}

      {entryAction && (
        <>
          <div className="fixed inset-0 z-50 bg-black/50" onClick={() => { if (!actionBusy) closeEntryActions(); }} aria-hidden="true" />
          <div className="fixed inset-x-0 bottom-0 z-50 flex max-h-[85vh] flex-col justify-end p-4 pb-[env(safe-area-inset-bottom)]">
            <div className="mx-auto w-full max-w-xl overflow-y-auto">
              <Card data-testid="entry-actions-sheet" className="flex flex-col gap-2 p-5" onClick={(event) => event.stopPropagation()}>
                <div className="flex items-center justify-between gap-3">
                  <h3 className="min-w-0 truncate text-lg font-semibold text-text">{entryAction.name}</h3>
                  <button
                    type="button"
                    data-testid="entry-actions-close"
                    aria-label="Close entry actions"
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-text-muted active:bg-muted select-none"
                    onClick={() => { if (!actionBusy) closeEntryActions(); }}
                  >
                    ×
                  </button>
                </div>
                {actionError && <p data-testid="entry-actions-error" role="alert" className="rounded-xl bg-danger/10 px-3 py-2 text-sm font-medium text-danger">{actionError}</p>}
                {actionNotice && <p data-testid="entry-actions-notice" role="status" className="rounded-xl bg-accent-light px-3 py-2 text-sm font-medium text-accent-dark">{actionNotice}</p>}
                {actionConfirmDelete ? (
                  <div className="flex flex-col gap-2 rounded-xl bg-danger/10 p-3" data-testid="entry-action-delete-confirm">
                    <p className="text-sm font-medium text-danger">Delete this entry? This cannot be undone.</p>
                    <div className="flex gap-2">
                      <Button variant="secondary" size="md" className="flex-1" disabled={actionBusy} onClick={() => setActionConfirmDelete(false)}>Cancel</Button>
                      <Button data-testid="entry-action-delete-final" variant="danger" size="md" className="flex-1" loading={actionBusy} onClick={() => void runActionDelete()}>Confirm Delete</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col">
                    {ENTRY_ACTIONS.map((action) => (
                      <button
                        key={action.key}
                        type="button"
                        data-testid={action.testId}
                        disabled={actionBusy}
                        className={cn(
                          "flex min-h-[44px] items-center rounded-xl px-3 text-left text-sm font-medium transition-all duration-200 ease-out active:bg-muted active:scale-[0.99] select-none disabled:opacity-50",
                          action.key === "delete" ? "text-danger" : "text-text",
                        )}
                        onClick={() => {
                          if (action.key === "edit") {
                            const entry = entryAction;
                            closeEntryActions();
                            setEditTarget({ kind: "food", entry });
                          } else if (action.key === "duplicate") {
                            void runDuplicate();
                          } else if (action.key === "move") {
                            setMovePickerOpen((open) => !open);
                          } else if (action.key === "saveLibrary") {
                            void runSaveToLibrary();
                          } else if (action.key === "saveMemory") {
                            void runSaveAsMemory();
                          } else {
                            void runActionDelete();
                          }
                        }}
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                )}
                {movePickerOpen && !actionConfirmDelete && (
                  <div className="grid grid-cols-2 gap-2 rounded-xl bg-muted/60 p-3" data-testid="entry-action-move-picker">
                    {MOVE_TARGET_TYPES.map((mealType) => (
                      <button
                        key={mealType}
                        type="button"
                        data-testid={`entry-action-move-${mealType}`}
                        disabled={actionBusy}
                        className="flex min-h-[44px] items-center justify-center rounded-xl bg-surface px-3 text-sm font-medium text-text transition-all duration-200 ease-out active:scale-[0.98] select-none disabled:opacity-50"
                        onClick={() => void runMove(mealType)}
                      >
                        {MEAL_GROUP_LABELS[mealType]}
                      </button>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </div>
        </>
      )}

      {copySheetOpen && (
        <>
          <div className="fixed inset-0 z-50 bg-black/50" onClick={() => { if (!copyBusy) setCopySheetOpen(false); }} aria-hidden="true" />
          <div className="fixed inset-x-0 bottom-0 z-50 flex max-h-[85vh] flex-col justify-end p-4 pb-[env(safe-area-inset-bottom)]">
            <div className="mx-auto w-full max-w-xl overflow-y-auto">
              <Card data-testid="copy-yesterday-sheet" className="flex flex-col gap-3 p-5" onClick={(event) => event.stopPropagation()}>
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-lg font-semibold text-text">Copy yesterday?</h3>
                  <button
                    type="button"
                    data-testid="copy-yesterday-cancel"
                    aria-label="Close copy yesterday sheet"
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-text-muted active:bg-muted select-none"
                    onClick={() => { if (!copyBusy) setCopySheetOpen(false); }}
                  >
                    ×
                  </button>
                </div>
                <p className="text-sm text-text-muted">
                  This adds yesterday&apos;s {yesterdayLogged.length} logged {yesterdayLogged.length === 1 ? "item" : "items"} to this day. Nothing is copied until you confirm.
                </p>
                <div className="flex max-h-56 flex-col gap-1 overflow-y-auto rounded-xl bg-muted/60 px-3 py-2" data-testid="copy-yesterday-items">
                  {yesterdayLogged.map((entry) => {
                    const kcal = entryKcal(entry);
                    return (
                      <div key={entry._id} className="flex items-center justify-between gap-3 text-sm">
                        <span className="min-w-0 truncate text-text">{entry.name}</span>
                        <span className="shrink-0 text-text-muted">{kcal === undefined ? "—" : `${kcal} kcal`}</span>
                      </div>
                    );
                  })}
                </div>
                {copyError && <p data-testid="copy-yesterday-error" role="alert" className="rounded-xl bg-danger/10 px-3 py-2 text-sm font-medium text-danger">{copyError}</p>}
                <Button data-testid="copy-yesterday-confirm" variant="primary" size="md" loading={copyBusy} onClick={() => void runCopyYesterday()}>
                  Copy to this day
                </Button>
              </Card>
            </div>
          </div>
        </>
      )}

      {editTarget && <div className="fixed inset-0 z-50 bg-black/50" onClick={handleClose} aria-hidden="true" />}
      {editTarget && (
        <div className="fixed inset-x-0 bottom-0 z-50 flex max-h-[85vh] flex-col justify-end p-4">
          <div className="mx-auto max-h-[85vh] w-full max-w-xl overflow-y-auto">
            <Card data-testid="edit-sheet" className="flex flex-col gap-4 p-5" onClick={(event) => event.stopPropagation()}>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-text">Edit {editTarget.kind === "food" ? "Meal" : "Exercise"}</h3>
                <button type="button" aria-label="Close" className="flex h-11 w-11 items-center justify-center rounded-full text-text-muted active:bg-muted select-none" onClick={handleClose}>×</button>
              </div>
              <Input label={editTarget.kind === "food" ? "Name" : "Type"} value={formName} onChange={(event) => setFormName(event.target.value)} placeholder={editTarget.kind === "food" ? "e.g. Toast" : "e.g. Running"} />
              <Input data-testid="edit-kcal-input" label="Calories (kcal)" type="number" inputMode="numeric" value={formKcal} onChange={(event) => { setFormKcal(event.target.value); setEditError(null); }} placeholder="0" />
              {editTarget.kind === "food" && (
                <div className="rounded-xl border border-border bg-muted/60 p-3" data-testid="edit-macros">
                  <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">Nutrition details</p>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <Input data-testid="edit-protein-input" label="Protein (g)" type="number" inputMode="decimal" min="0" value={formProtein} onChange={(event) => setFormProtein(event.target.value)} placeholder="Keep current" />
                    <Input data-testid="edit-carbs-input" label="Carbs (g)" type="number" inputMode="decimal" min="0" value={formCarbs} onChange={(event) => setFormCarbs(event.target.value)} placeholder="Keep current" />
                    <Input data-testid="edit-fat-input" label="Fat (g)" type="number" inputMode="decimal" min="0" value={formFat} onChange={(event) => setFormFat(event.target.value)} placeholder="Keep current" />
                    <Input data-testid="edit-fiber-input" label="Fiber (g)" type="number" inputMode="decimal" min="0" value={formFiber} onChange={(event) => setFormFiber(event.target.value)} placeholder="Keep current" />
                  </div>
                  {editError && <p className="mt-2 text-sm text-danger" role="alert" data-testid="edit-error">{editError}</p>}
                </div>
              )}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-text">Notes</label>
                <textarea value={formNotes} onChange={(event) => setFormNotes(event.target.value)} placeholder="Add details..." rows={3} className={cn("w-full resize-none rounded-xl border border-border bg-surface py-3 px-4 text-base", "text-text placeholder:text-text-muted", "focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent")} />
              </div>
              {confirmingDelete ? (
                <div className="flex flex-col gap-2 rounded-xl bg-danger/10 p-3">
                  <p className="text-sm font-medium text-danger">Delete this {editTarget.kind}? This cannot be undone.</p>
                  <div className="flex gap-2">
                    <Button variant="secondary" size="md" className="flex-1" onClick={() => setConfirmingDelete(false)}>Cancel</Button>
                    <Button data-testid="edit-delete-btn" variant="danger" size="md" className="flex-1" loading={saving} onClick={handleDelete}>Confirm Delete</Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  <Button data-testid="edit-save-btn" variant="primary" size="md" loading={saving} onClick={handleSave}>Save</Button>
                  <Button data-testid="edit-delete-btn" variant="danger" size="md" onClick={handleDelete}>Delete</Button>
                </div>
              )}
              {editTarget.kind !== "food" && editError && <p className="text-sm text-danger" role="alert" data-testid="edit-error">{editError}</p>}
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
